import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useMediaQuery } from 'react-responsive';
import 'react-circular-progressbar/dist/styles.css';
import 'react-phone-input-2/lib/style.css';

import Text from 'common/components/Text/Text';
import { ACADEMIC_TESTS } from 'common/constants';

import { CommonHash } from 'app/types';
import styles from './UserInformation.module.scss';
import UserFileUpload from './UserFileUpload';

const academicTestOptions = ACADEMIC_TESTS.map(([label, value]) => ({
  label: label,
  value: value,
}));

const currentYear: number = new Date().getFullYear();
const years = [];
for (let i = currentYear; i >= currentYear - 60; i--) {
  years.push(i);
}

interface CompProps {
  index: number;
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
}

interface SATProps {
  technicalAnswers: CommonHash;
  setInputOption: (key: string, value: any) => void;
}

const SATForm = ({ technicalAnswers, setInputOption }: SATProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <>
      <div className={styles.inputGroupRow} style={{ width: '100%' }}>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={isMobile ? 14 : 16}
            weight={isMobile ? 400 : 500}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Evidence-Based Reading and Writing section
          </Text>
          <input
            className="form-control"
            type="number"
            onChange={(e) => setInputOption('technical_exam_writing', e.target.value)}
            value={
              technicalAnswers.technical_exam_writing ? technicalAnswers.technical_exam_writing : ''
            }
          />
        </div>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={isMobile ? 14 : 16}
            weight={isMobile ? 400 : 500}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Math section
          </Text>
          <input
            className="form-control"
            type="number"
            onChange={(e) => setInputOption('technical_exam_math', e.target.value)}
            value={technicalAnswers.technical_exam_math ? technicalAnswers.technical_exam_math : ''}
          />
        </div>
      </div>
    </>
  );
};

const TechnicalForm = ({ index, answers, setAnswer }: CompProps): React.ReactElement => {
  const [technicalAnswers, setTechnicalAnswers] = useState<CommonHash>({});
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const setInputOption = (key: string, value: any) => {
    setTechnicalAnswers((a: any) => ({ ...a, [key]: value }));
  };

  useEffect(() => {
    if (answers['technical_exams'] && answers['technical_exams'][index]) {
      setTechnicalAnswers(answers['technical_exams'][index]);
    }
  }, []);

  useEffect(() => {
    if (answers['technical_exams']) {
      const list = [...answers['technical_exams']];
      list.splice(index, 1, technicalAnswers);
      setAnswer((a: any) => ({
        ...a,
        ['technical_exams']: list,
      }));
    }
  }, [technicalAnswers]);

  return (
    <div className={styles.inputsContainter}>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          Test type
        </Text>
        <Select
          isSearchable
          classNamePrefix="react-select"
          className={styles.selectInput}
          options={academicTestOptions}
          value={academicTestOptions.find(
            (o: any) => o.value === technicalAnswers.technical_exam_type
          )}
          onChange={({ value }: any) => setInputOption('technical_exam_type', value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          {isMobile ? 'Score' : 'Test score'}
        </Text>
        <input
          className="form-control"
          type="number"
          onChange={(e) => setInputOption('score_technical_exam', e.target.value)}
          value={technicalAnswers.score_technical_exam ? technicalAnswers.score_technical_exam : ''}
        />
      </div>
      {technicalAnswers.technical_exam === 'sat' ? (
        <SATForm technicalAnswers={technicalAnswers} setInputOption={setInputOption} />
      ) : (
        <>
          <div className={styles.inputGroupRow}>
            <div className={styles.inputGroup} style={{ width: '48%' }}>
              <Text
                size={isMobile ? 14 : 16}
                weight={isMobile ? 400 : 500}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Reading
              </Text>
              <input
                className="form-control"
                type="number"
                onChange={(e) => setInputOption('technical_exam_reading', e.target.value)}
                value={
                  technicalAnswers.technical_exam_reading
                    ? technicalAnswers.technical_exam_reading
                    : ''
                }
              />
            </div>
            <div className={styles.inputGroup} style={{ width: '48%' }}>
              <Text
                size={isMobile ? 14 : 16}
                weight={isMobile ? 400 : 500}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Writing
              </Text>
              <input
                className="form-control"
                type="number"
                onChange={(e) => setInputOption('technical_exam_writing', e.target.value)}
                value={
                  technicalAnswers.technical_exam_writing
                    ? technicalAnswers.technical_exam_writing
                    : ''
                }
              />
            </div>
          </div>
          <div className={styles.inputGroupRow}>
            <div className={styles.inputGroup} style={{ width: '48%' }}>
              <Text
                size={isMobile ? 14 : 16}
                weight={isMobile ? 400 : 500}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Language
              </Text>
              <input
                className="form-control"
                type="number"
                onChange={(e) => setInputOption('technical_exam_language', e.target.value)}
                value={
                  technicalAnswers.technical_exam_language
                    ? technicalAnswers.technical_exam_language
                    : ''
                }
              />
            </div>
            <div className={styles.inputGroup} style={{ width: '48%' }}>
              <Text
                size={isMobile ? 14 : 16}
                weight={isMobile ? 400 : 500}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Math
              </Text>
              <input
                className="form-control"
                type="number"
                onChange={(e) => setInputOption('technical_exam_math', e.target.value)}
                value={
                  technicalAnswers.technical_exam_math ? technicalAnswers.technical_exam_math : ''
                }
              />
            </div>
          </div>
        </>
      )}
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          Date of issue
        </Text>
        <input
          className="form-control"
          type="date"
          onChange={(e) => setInputOption('technical_exam_date', e.target.value)}
          value={technicalAnswers.technical_exam_date ? technicalAnswers.technical_exam_date : ''}
        />
      </div>
      <UserFileUpload
        setInputOptionOfForm={setInputOption}
        fileType="technical_exam"
        answers={answers['technical_exams'][index]}
        index={index}
      />
    </div>
  );
};

export default TechnicalForm;
